// @ts-check

/**
 * Typed client for the MEET API. Every wire contract lives in
 * meetApiTypes.d.ts; this file is checked against them via `// @ts-check`
 * (run `npm run typecheck`).
 *
 * @typedef {import('./meetApiTypes').Profile} Profile
 * @typedef {import('./meetApiTypes').BadgeResponse} BadgeResponse
 * @typedef {import('./meetApiTypes').SuccessResponse} SuccessResponse
 * @typedef {import('./meetApiTypes').ContactRow} ContactRow
 * @typedef {import('./meetApiTypes').AddContactResponse} AddContactResponse
 * @typedef {import('./meetApiTypes').ScannedByRow} ScannedByRow
 * @typedef {import('./meetApiTypes').BadgeSummary} BadgeSummary
 * @typedef {import('./meetApiTypes').MeetEvent} MeetEvent
 * @typedef {import('./meetApiTypes').EventAdminsResponse} EventAdminsResponse
 * @typedef {import('./meetApiTypes').EventAttendeesResponse} EventAttendeesResponse
 * @typedef {import('./meetApiTypes').AccessAdminResponse} AccessAdminResponse
 * @typedef {import('./meetApiTypes').MyAccessResponse} MyAccessResponse
 * @typedef {import('./meetApiTypes').MigrationStatus} MigrationStatus
 * @typedef {import('./meetApiTypes').RunMigrationsResponse} RunMigrationsResponse
 * @typedef {import('./meetApiTypes').HealthResponse} HealthResponse
 */

const BASE = '/api/meet';

// The app calls setAuthTokenProvider() once Clerk is mounted; the function
// returns a fresh JWT (or null) for each outgoing request.
/** @type {null | (() => Promise<string | null>)} */
let authTokenProvider = null;

/** @param {null | (() => Promise<string | null>)} fn */
export const setAuthTokenProvider = (fn) => { authTokenProvider = fn; };

/**
 * Issue a request against the MEET API. Attaches a Clerk bearer token
 * when a provider is registered. Throws Error(message) on a non-2xx.
 *
 * @param {string} path - path under /api/meet
 * @param {{ method?: string, body?: string, headers?: Record<string, string> }} [options]
 * @returns {Promise<any>} the parsed JSON body
 */
const request = async (path, options = {}) => {
  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (authTokenProvider) {
    try {
      const token = await authTokenProvider();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {}
  }
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
};

export const meetApi = {
  // Badge profiles

  /** @param {string} code @returns {Promise<BadgeResponse>} */
  getBadge: (code) => request(`/badge/${code}`),
  /** @param {string} code @param {Profile} profile @returns {Promise<SuccessResponse>} */
  claimBadge: (code, profile) => request(`/badge/${code}`, { method: 'POST', body: JSON.stringify(profile) }),
  /** @param {string} code @param {Profile} profile @returns {Promise<SuccessResponse>} */
  updateBadge: (code, profile) => request(`/badge/${code}`, { method: 'PUT', body: JSON.stringify(profile) }),

  // Contacts

  /** @param {string} clerkUserId @returns {Promise<ContactRow[]>} */
  getMyContacts: (clerkUserId) => request(`/users/${encodeURIComponent(clerkUserId)}/contacts`),
  /** @param {{ scannedCode: string, eventId?: string|null, clerkUserId?: string }} data @returns {Promise<AddContactResponse>} */
  addContact: (data) => request('/contacts', { method: 'POST', body: JSON.stringify(data) }),
  /** @param {number} id @param {string} note @returns {Promise<SuccessResponse>} */
  updateContactNote: (id, note) => request(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify({ note }) }),
  /** @param {number} id @param {boolean} followUp @returns {Promise<SuccessResponse>} */
  setContactFollowUp: (id, followUp) => request(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify({ followUp }) }),
  /** @param {number} id @returns {Promise<SuccessResponse>} */
  deleteContact: (id) => request(`/contacts/${id}`, { method: 'DELETE' }),

  // Reciprocal scans: record that I viewed someone's badge, and list people
  // who viewed mine that I haven't added back yet.

  /** @param {string} scannedCode @returns {Promise<SuccessResponse>} */
  recordScan: (scannedCode) => request('/scan', { method: 'POST', body: JSON.stringify({ scannedCode }) }),
  /** @param {string} clerkUserId @returns {Promise<ScannedByRow[]>} */
  getScannedBy: (clerkUserId) => request(`/users/${encodeURIComponent(clerkUserId)}/scanned-by`),

  // Events

  /** @returns {Promise<MeetEvent[]>} */
  getEvents: () => request('/events'),
  /** @param {{ name: string, [key: string]: unknown }} data @returns {Promise<MeetEvent>} */
  createEvent: (data) => request('/events', { method: 'POST', body: JSON.stringify(data) }),
  /** @param {string} eventId @returns {Promise<MeetEvent>} */
  getEvent: (eventId) => request(`/events/${eventId}`),
  /** @param {string} eventId @param {Record<string, unknown>} data @returns {Promise<MeetEvent>} */
  updateEvent: (eventId, data) => request(`/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** @param {string} eventId @returns {Promise<SuccessResponse>} */
  deleteEvent: (eventId) => request(`/events/${eventId}`, { method: 'DELETE' }),
  /** @param {string} eventId @returns {Promise<any>} */
  listEventBadges: (eventId) => request(`/events/${eventId}/badges`),
  /** @param {string} eventId @param {number} count @param {string} [prefix] @returns {Promise<any>} */
  generateEventBadges: (eventId, count, prefix) =>
    request(`/events/${eventId}/badges`, { method: 'POST', body: JSON.stringify({ count, prefix }) }),
  /** @param {string} eventId @returns {Promise<EventAdminsResponse>} */
  getEventAdmins: (eventId) => request(`/events/${eventId}/admins`),
  /** @param {string} eventId @returns {Promise<EventAttendeesResponse>} */
  getEventAttendees: (eventId) => request(`/events/${eventId}/attendees`),
  /** @param {string} eventId @param {{ email?: string, clerkUserId?: string }} data @returns {Promise<EventAdminsResponse>} */
  addEventAdmin: (eventId, data) => request(`/events/${eventId}/admins`, { method: 'POST', body: JSON.stringify(data) }),
  /** @param {string} eventId @param {number} adminId @returns {Promise<EventAdminsResponse>} */
  removeEventAdmin: (eventId, adminId) =>
    request(`/events/${eventId}/admins`, { method: 'DELETE', body: JSON.stringify({ adminId }) }),
  /** @param {string} clerkUserId @returns {Promise<MeetEvent[]>} */
  getUserEvents: (clerkUserId) => request(`/users/${encodeURIComponent(clerkUserId)}/events`),
  /** @param {string} clerkUserId @returns {Promise<BadgeSummary[]>} */
  getMyBadges: (clerkUserId) => request(`/users/${encodeURIComponent(clerkUserId)}/badges`),
  /** @param {string} newCode @returns {Promise<SuccessResponse>} */
  replaceBadge: (newCode) => request('/replace-badge', { method: 'POST', body: JSON.stringify({ newCode }) }),
  /** @param {string} code @returns {Promise<SuccessResponse>} */
  linkBadge: (code) => request('/users/me/link-badge', { method: 'POST', body: JSON.stringify({ code }) }),
  /** @returns {Promise<{ linked?: string[] }>} */
  autoLinkBadges: () => request('/users/me/auto-link-badges', { method: 'POST', body: '{}' }),
  /** @param {string} code @returns {Promise<{ hasContact: boolean }>} */
  hasContact: (code) => request(`/users/me/has-contact?code=${encodeURIComponent(code)}`),
  /** @returns {Promise<any>} */
  exportMyData: () => request('/users/me/export'),
  /** @returns {Promise<SuccessResponse>} */
  deleteMyData: () => request('/users/me/delete-data', { method: 'POST', body: JSON.stringify({ confirm: true }) }),
  /** @returns {Promise<MigrationStatus>} */
  getMigrations: () => request('/admin/migrate'),
  /** @returns {Promise<RunMigrationsResponse>} */
  runMigrations: () => request('/admin/migrate', { method: 'POST', body: '{}' }),

  // Event-creation access control

  /** @returns {Promise<MyAccessResponse>} */
  getMyAccess: () => request('/access/me'),
  /** @param {{ name?: string, email?: string, organization?: string, message?: string }} data @returns {Promise<SuccessResponse>} */
  requestEventAccess: (data) => request('/access/request', { method: 'POST', body: JSON.stringify(data) }),
  /** @returns {Promise<AccessAdminResponse>} */
  getAccessAdmin: () => request('/admin/access'),
  /** @param {{ action: string, [key: string]: unknown }} body @returns {Promise<Partial<AccessAdminResponse>>} */
  accessAdminAction: (body) => request('/admin/access', { method: 'POST', body: JSON.stringify(body) }),

  // Health

  /** @returns {Promise<HealthResponse>} */
  health: () => request('/health'),
};

export default meetApi;
